import { useMemo } from "react";
import type { UIMessage } from "@ai-sdk/react";

export interface ProgressStep {
  name: string;
  status: "pending" | "active" | "done";
}

interface ProgressDataPart {
  type: "data-progress";
  data: { steps: ProgressStep[] };
}

function isProgressPart(part: unknown): part is ProgressDataPart {
  return (
    typeof part === "object" &&
    part !== null &&
    (part as Record<string, unknown>).type === "data-progress"
  );
}

export function useChatProgress(messages: UIMessage[] | undefined): ProgressStep[] {
  return useMemo(() => {
    if (!messages || messages.length === 0) return [];

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]!;
      if (!msg.parts) continue;

      for (let j = msg.parts.length - 1; j >= 0; j--) {
        const part = msg.parts[j]!;
        if (isProgressPart(part)) {
          const steps = part.data.steps;
          const allDone = steps.length > 0 && steps.every((s) => s.status === "done");

          if (allDone) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg?.role === "user" && i < messages.length - 1) {
              return [];
            }
          }

          // Hide stale progress: if there are newer assistant or user
          // messages after this progress, the task has moved on.
          const hasNewerMessages = messages.slice(i + 1).some(
            (m) => m.role === "user" || m.role === "assistant",
          );
          if (hasNewerMessages && !allDone) {
            return [];
          }

          return steps;
        }
      }
    }

    return [];
  }, [messages]);
}
